import { useMemo } from 'react'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import type { MonthlyRowEntry } from './interfaces/MonthlyRecord'
import { calcTotals } from './monthlyRecordsHelpers'
import {
  SNAPSHOT_COMPARABLE_FIELDS,
  type SnapshotComparableField,
} from './monthlyRecordsState'

const COMPARABLE_FIELDS = SNAPSHOT_COMPARABLE_FIELDS

export type ComparableField = SnapshotComparableField

const FIELD_LABELS: Record<ComparableField, string> = {
  price_monthly: 'Rent (฿)',
  elec_unit_price: 'Elec ฿/Unit',
  elec_prev_reading: 'Elec Prev',
  elec_curr_reading: 'Elec Curr',
  water_unit_price: 'Water ฿/Unit',
  water_prev_reading: 'Water Prev',
  water_curr_reading: 'Water Curr',
  internet_fee: 'Internet',
  furniture_fee: 'Furniture',
  maintenance_fee: 'Maintain',
  other_fee: 'Other',
  other_fee_label: 'Other Label',
  record_date: 'Date',
  payment_status: 'Status',
  remark: 'Remark',
  room_type: 'Room Type',
  room_status: 'Room Status',
  room_remark: 'Room Note',
  repair_details: 'Repair Details',
  keycards: 'Keycards',
}

interface SavePreviewModalProps {
  rows: MonthlyRowEntry[]
  snapshotRoomCount?: number
  onConfirm: (includeSystemChanges: boolean) => void
  onCancel: () => void
  onLoadMissingFromSource?: (
    roomFields: Record<string, ComparableField[]>,
  ) => void
  isPending: boolean
}

type PreviewValue = {
  value: string | null
  missing: boolean
}

function getFormValue(
  values: MonthlyRowEntry['form'],
  field: ComparableField,
): string {
  switch (field) {
    case 'price_monthly':
      return values.price_monthly
    case 'internet_fee':
      return values.internet_fee
    case 'furniture_fee':
      return values.furniture_fee
    case 'maintenance_fee':
      return values.maintenance_fee
    case 'other_fee':
      return values.other_fee
    case 'other_fee_label':
      return values.other_fee_label
    case 'room_type':
      return values.room_type
    case 'room_status':
      return values.room_status
    case 'room_remark':
      return values.room_remark
    case 'repair_details':
      return values.repair_details
    case 'keycards':
      return values.keycards
    default:
      return values[field]
  }
}

function getBeforePreviewValue(
  entry: MonthlyRowEntry,
  field: ComparableField,
): PreviewValue {
  if (entry.source === 'snapshot') {
    const missing = entry.missingSnapshotFields?.includes(field) ?? false
    return {
      value: entry.snapshotForm
        ? getFormValue(entry.snapshotForm, field)
        : null,
      missing,
    }
  }

  if (!entry.existingRecord) return { value: null, missing: false }

  const rec = entry.existingRecord
  switch (field) {
    case 'price_monthly':
      return { value: String(rec.room.price_monthly), missing: false }
    case 'elec_unit_price':
      return { value: String(rec.util.elec_unit_price), missing: false }
    case 'elec_prev_reading':
      return { value: String(rec.util.elec_prev_reading), missing: false }
    case 'elec_curr_reading':
      return { value: String(rec.util.elec_curr_reading), missing: false }
    case 'water_unit_price':
      return { value: String(rec.util.water_unit_price), missing: false }
    case 'water_prev_reading':
      return { value: String(rec.util.water_prev_reading), missing: false }
    case 'water_curr_reading':
      return { value: String(rec.util.water_curr_reading), missing: false }
    case 'internet_fee':
      return { value: String(rec.room.internet_fee), missing: false }
    case 'furniture_fee':
      return { value: String(rec.room.furniture_fee), missing: false }
    case 'maintenance_fee':
      return { value: String(rec.room.maintenance_fee), missing: false }
    case 'other_fee':
      return { value: String(rec.room.other_fee), missing: false }
    case 'other_fee_label':
      return { value: rec.room.other_fee_label ?? '', missing: false }
    case 'record_date':
      return { value: rec.util.record_date, missing: false }
    case 'payment_status':
      return { value: rec.util.payment_status, missing: false }
    case 'remark':
      return { value: rec.util.remark ?? '', missing: false }
    case 'room_type':
      return { value: rec.room.room_type, missing: false }
    case 'room_status':
      return { value: rec.room.status, missing: false }
    case 'room_remark':
      return { value: rec.room.remark ?? '', missing: false }
    case 'repair_details':
      return { value: rec.room.repair_details ?? '', missing: false }
    case 'keycards':
      return { value: rec.room.keycards?.join(', ') ?? '', missing: false }
    default:
      return { value: null, missing: false }
  }
}

function getAfterValue(entry: MonthlyRowEntry, field: ComparableField): string {
  switch (field) {
    case 'price_monthly':
      return entry.form.price_monthly
    case 'internet_fee':
      return entry.form.internet_fee
    case 'furniture_fee':
      return entry.form.furniture_fee
    case 'maintenance_fee':
      return entry.form.maintenance_fee
    case 'other_fee':
      return entry.form.other_fee
    case 'other_fee_label':
      return entry.form.other_fee_label
    case 'room_type':
      return entry.form.room_type
    case 'room_status':
      return entry.form.room_status
    case 'room_remark':
      return entry.form.room_remark
    case 'repair_details':
      return entry.form.repair_details
    case 'keycards':
      return entry.form.keycards
    default:
      return entry.form[field]
  }
}

function getTenantName(entry: MonthlyRowEntry): string {
  if (entry.tenantName) return entry.tenantName
  if (!entry.contract?.tenant) return '—'
  const name =
    `${entry.contract.tenant.first_name ?? ''} ${entry.contract.tenant.last_name ?? ''}`.trim()
  return name || '—'
}

export function getPreviewDiffFields(
  rows: MonthlyRowEntry[],
): ComparableField[] {
  const dirtyRows = rows.filter((r) => r.dirty)
  const diffFieldSet = new Set<ComparableField>()

  for (const entry of dirtyRows) {
    for (const field of COMPARABLE_FIELDS) {
      const { value: before } = getBeforePreviewValue(entry, field)
      const after = getAfterValue(entry, field)
      const hasDiff = before === null ? after !== '' : before !== after

      if (hasDiff) {
        diffFieldSet.add(field)
      }
    }
  }

  return COMPARABLE_FIELDS.filter((f) => diffFieldSet.has(f))
}

export function SavePreviewModal({
  rows,
  snapshotRoomCount,
  onConfirm,
  onCancel,
  onLoadMissingFromSource,
  isPending,
}: SavePreviewModalProps) {
  const previewRows = useMemo(() => rows.filter((row) => row.dirty), [rows])
  const newRows = previewRows.filter(
    (row) => row.source === 'live' && !row.existingRecord,
  )
  const editedRows = previewRows.filter(
    (row) => row.source === 'snapshot' || row.existingRecord,
  )
  const diffFields = getPreviewDiffFields(previewRows)
  const totalChanges = previewRows.length
  const missingFieldsByRoom = previewRows.reduce<
    Record<string, ComparableField[]>
  >((acc, row) => {
    if (row.source !== 'snapshot') return acc

    const missingFields = diffFields.filter((field) =>
      row.missingSnapshotFields?.includes(field),
    )
    if (missingFields.length > 0) {
      acc[row.room.room_number] = missingFields
    }
    return acc
  }, {})
  const hasMissingSnapshotFields = Object.keys(missingFieldsByRoom).length > 0

  // Helper to render table rows for a given set of entries
  const renderRows = (
    entries: MonthlyRowEntry[],
    badgeType: 'new' | 'edit',
  ) => {
    return entries.map((entry) => {
      const beforeForm =
        entry.source === 'snapshot' ? entry.snapshotForm : undefined
      const beforeTotals = beforeForm
        ? calcTotals(beforeForm)
        : entry.existingRecord
          ? calcTotals({
              elec_unit_price: String(
                entry.existingRecord.util.elec_unit_price,
              ),
              elec_prev_reading: String(
                entry.existingRecord.util.elec_prev_reading,
              ),
              elec_curr_reading: String(
                entry.existingRecord.util.elec_curr_reading,
              ),
              water_unit_price: String(
                entry.existingRecord.util.water_unit_price,
              ),
              water_prev_reading: String(
                entry.existingRecord.util.water_prev_reading,
              ),
              water_curr_reading: String(
                entry.existingRecord.util.water_curr_reading,
              ),
              record_date: entry.existingRecord.util.record_date,
              payment_status: entry.existingRecord.util.payment_status as
                | 'paid'
                | 'unpaid',
              remark: entry.existingRecord.util.remark ?? '',
              price_monthly: String(entry.existingRecord.room.price_monthly),
              internet_fee: String(entry.existingRecord.room.internet_fee),
              furniture_fee: String(entry.existingRecord.room.furniture_fee),
              maintenance_fee: String(
                entry.existingRecord.room.maintenance_fee,
              ),
              other_fee: String(entry.existingRecord.room.other_fee),
              other_fee_label: entry.existingRecord.room.other_fee_label ?? '',
              room_type: entry.existingRecord.room.room_type,
              room_status: entry.existingRecord.room.status,
              room_remark: entry.existingRecord.room.remark ?? '',
              repair_details: entry.existingRecord.room.repair_details ?? '',
              keycards: (entry.existingRecord.room.keycards ?? []).join(', '),
            })
          : null
      const afterTotals = calcTotals(entry.form)
      const isNew = entry.source === 'live' && !entry.existingRecord
      const grandChanged =
        beforeTotals !== null &&
        beforeTotals.grand_total !== afterTotals.grand_total

      const badgeColor =
        badgeType === 'new'
          ? 'bg-blue-100 text-blue-700'
          : 'bg-amber-100 text-amber-700'

      return (
        <tr key={entry.room.room_number} className="hover:bg-gray-50">
          <td className="whitespace-nowrap px-3 py-3 font-medium">
            {entry.room.room_number}
          </td>
          <td className="whitespace-nowrap px-3 py-3 text-gray-600">
            {getTenantName(entry)}
          </td>

          {diffFields.map((field) => {
            const { value: before, missing } = getBeforePreviewValue(
              entry,
              field,
            )
            const after = getAfterValue(entry, field)
            const fieldIsNew =
              entry.source === 'live' &&
              before === null &&
              !entry.existingRecord
            const fieldChanged = !fieldIsNew && before !== after

            return (
              <td key={field} className="px-3 py-3">
                {missing && fieldChanged ? (
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium text-red-500 text-xs">
                      No snapshot
                    </span>
                    <span className="font-medium text-gray-900">
                      {after || '—'}
                    </span>
                  </div>
                ) : fieldIsNew ? (
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 font-semibold text-xs ${badgeColor}`}
                    >
                      New
                    </span>
                    <span className="text-gray-700">{after || '—'}</span>
                  </div>
                ) : fieldChanged ? (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-gray-400 text-xs line-through">
                      {before || '—'}
                    </span>
                    <span className="font-medium text-gray-900">
                      {after || '—'}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </td>
            )
          })}

          {/* Grand total */}
          <td className="px-3 py-3 text-right tabular-nums">
            {isNew ? (
              <div className="flex items-center justify-end gap-1.5">
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 font-semibold text-xs ${badgeColor}`}
                >
                  New
                </span>
                <span className="font-semibold text-green-700">
                  {afterTotals.grand_total.toLocaleString()}
                </span>
              </div>
            ) : grandChanged ? (
              <div className="flex flex-col items-end gap-0.5">
                <span className="text-gray-400 text-xs line-through">
                  {beforeTotals?.grand_total.toLocaleString()}
                </span>
                <span className="font-semibold text-green-700">
                  {afterTotals.grand_total.toLocaleString()}
                </span>
              </div>
            ) : (
              <span className="font-medium">
                {afterTotals.grand_total.toLocaleString()}
              </span>
            )}
          </td>
        </tr>
      )
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="flex w-full max-w-4xl flex-col gap-4 p-6">
        {/* Header */}
        <div>
          <h3 className="font-semibold text-gray-900 text-lg">
            Preview Changes
          </h3>
          <p className="text-gray-500 text-sm">
            {totalChanges} change{totalChanges !== 1 ? 's' : ''} detected . A
            snapshot will be created. Review before confirming.
          </p>
        </div>

        {/* Diff table */}
        <div className="max-h-[60vh] overflow-y-auto rounded-md border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr className="border-gray-200 border-b">
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-500">
                  Room
                </th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-500">
                  Tenant
                </th>
                {diffFields.map((field) => (
                  <th
                    key={field}
                    className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-500"
                  >
                    {FIELD_LABELS[field]}
                  </th>
                ))}
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium text-gray-500">
                  Grand Total (฿)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {/* New Rows Section */}
              {newRows.length > 0 && (
                <>
                  <tr className="bg-blue-50">
                    <td
                      colSpan={diffFields.length + 3}
                      className="px-3 py-2 font-semibold text-blue-900 text-xs uppercase tracking-wide"
                    >
                      🔵 New Rows ({newRows.length})
                    </td>
                  </tr>
                  {renderRows(newRows, 'new')}
                </>
              )}

              {/* Edited Rows Section */}
              {editedRows.length > 0 && (
                <>
                  <tr className="bg-amber-50">
                    <td
                      colSpan={diffFields.length + 3}
                      className="px-3 py-2 font-semibold text-amber-900 text-xs uppercase tracking-wide"
                    >
                      🟠 Edited Rows ({editedRows.length})
                    </td>
                  </tr>
                  {renderRows(editedRows, 'edit')}
                </>
              )}
            </tbody>
          </table>
        </div>

        {snapshotRoomCount !== undefined && (
          <p className="text-gray-600 text-sm">
            Save {totalChanges} changed room{totalChanges !== 1 ? 's' : ''} in a{' '}
            {snapshotRoomCount}-room monthly snapshot. Untouched rooms keep
            their saved values.
          </p>
        )}
        {/* Footer */}
        <div className="flex flex-wrap justify-end gap-2 border-gray-100 border-t pt-2">
          {hasMissingSnapshotFields && onLoadMissingFromSource && (
            <Button
              variant="outline"
              onClick={() => onLoadMissingFromSource(missingFieldsByRoom)}
              disabled={isPending}
            >
              Load Missing From Source
            </Button>
          )}
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(false)}
            disabled={isPending || totalChanges === 0}
          >
            {isPending ? 'Saving…' : 'Confirm & Save'}
          </Button>
        </div>
      </Card>
    </div>
  )
}
